import React from "react";

//QUERY

import { useQueryClient } from "@tanstack/react-query";

//ICONS

import { Hotspot, Scene } from "../../Types";
import { UseGetTour } from "../../api/calls/Queries.ts";
import IconSound from "../Icons/IconSound.tsx";
import { useGetQuiz } from "../../api/calls/Quiz.ts";
import {
  useCurrentSceneStore,
  useEditingElementStore,
  useMapEditorStore,
  useParametersStore,
  useWidePopupStore,
} from "../../api/Zustand.ts";

import * as THREE from "three";

import { useRightSidePanelStore } from "../../state management/zustand/editor-elements-stores.ts";
import { useGLRendererStore } from "../../state management/zustand/gl-stores.ts";
import {
  GearIcon,
  GroupsIcon,
  Link,
  MapIcon,
  PopupAddIcon,
  QuizAddIcon,
  RightArrow,
} from "../Icons/left-side-bar-icons.tsx";
import { getLanguageFile } from "../common/GetLanguageFile.ts";

const lookingAtVec = new THREE.Vector3();
type hsType = "transition" | "link" | "quiz" | "popup" | "audio";

const LeftSidePanel = () => {
  const { currentSceneID } = useCurrentSceneStore();

  const { tourID } = useParametersStore();

  const camera = useGLRendererStore((state) => state.state?.camera);

  const { setEditingElem } = useEditingElementStore();

  const { data: tourData } = UseGetTour(tourID);
  // const { tourData } = useTourDataStore();

  const { data: QuizData } = useGetQuiz(tourID, tourData?.assignedQuizID);

  const queryClient = useQueryClient();

  const mapEditor = useMapEditorStore();

  const rightPanel = useRightSidePanelStore();

  const currentScene = React.useMemo(() => {
    return tourData?.scenes.find((one) => one.id === currentSceneID);
  }, [tourData?.scenes, currentSceneID]);

  const hsDisabed: boolean = React.useMemo(() => {
    const isDisabled =
      tourData?.scenes?.length === 0 ||
      !currentScene?.imageID ||
      !!currentScene?.hotspots.find(
        (one: Hotspot) => one.isNew || one.EDIT_POS
      );
    return isDisabled;
  }, [currentScene, tourData?.scenes.length]);

  const quizDisabled: boolean = React.useMemo(() => {
    const isDisabled =
      !tourData?.assignedQuizID ||
      !QuizData?.success ||
      tourData?.scenes?.length === 0 ||
      !currentScene?.imageID ||
      !!currentScene!.hotspots.find(
        (one: Hotspot) => one.isNew || one.EDIT_POS
      );
    return isDisabled;
  }, [
    tourData?.assignedQuizID,
    QuizData?.success,
    tourData?.scenes,
    currentScene,
  ]);

  const wayDisabled: boolean = React.useMemo(() => {
    const isDisabled =
      tourData?.scenes?.length! < 2 ||
      !currentScene?.imageID ||
      !!currentScene?.hotspots.find(
        (one: Hotspot) => one.isNew || one.EDIT_POS
      );
    return isDisabled;
  }, [currentScene, tourData?.scenes]);

  const onHotspostAdd = React.useCallback(
    (type: hsType) => {
      const number = Math.floor(Math.random() * 10000000000);

      camera?.getWorldDirection(lookingAtVec);
      lookingAtVec.multiplyScalar(255);

      let defaultTitle;
      switch (type) {
        case "transition":
          defaultTitle = "waypoint";
          break;
        default:
          defaultTitle = type;
      }

      if (
        currentScene!.hotspots.find((one: Hotspot) => one.isNew || one.EDIT_POS)
      ) {
        return;
      }

      if (
        currentScene!.hotspots
          .filter((one: Hotspot) => one.type === type)
          .find((one: Hotspot) => one.isNew || one.EDIT_POS)
      ) {
        setEditingElem(
          type === "transition" ? "transition" : type,
          `${currentSceneID + type}#${Math.floor(Math.random() * 1000000000)}`
        );

        return;
      }

      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: tourData!.scenes.map((scene: Scene) =>
          scene.id === currentSceneID
            ? {
                ...scene,
                hotspots: [
                  ...currentScene!.hotspots,
                  {
                    id: `${currentSceneID + type}#${Math.floor(number)}`,
                    title: "Nuovo " + defaultTitle,
                    type: type === "transition" ? "transition" : type,
                    target: "",
                    x: lookingAtVec.x,
                    y: lookingAtVec.y,
                    z: lookingAtVec.z,
                    pos: lookingAtVec,
                    EDIT_POS: true,
                    isNew: true,
                    right: type === "transition" ? true : undefined,
                    dir: type === "transition" ? "L" : undefined,
                  },
                ],
              }
            : scene
        ),
        popups:
          type === "popup"
            ? [
                ...tourData!.popups,
                {
                  id: `${currentSceneID + type}#${number}`,
                  media: "",
                  video: "",
                  type: "",
                  title: "",
                  content: "",
                },
              ]
            : [...tourData!.popups],
      });

      setEditingElem(
        type === "transition" ? "transition" : type,
        `${currentSceneID + type}#${number}`
      );
      rightPanel.setOpen(true);
    },
    [
      camera,
      currentScene,
      currentSceneID,
      // lookingAt,
      tourID,
      queryClient,

      setEditingElem,
      tourData,
      rightPanel,
    ]
  );

  const changeEditElem = React.useCallback(
    (type: "groups" | "tour") => {
      rightPanel.setOpen(true);

      setEditingElem(type, type === "tour" ? type : "");
    },
    [rightPanel, setEditingElem]
  );

  const { isTakingScreenshot } = useWidePopupStore();

  const btnIsDisabled: (type: "transition" | "quiz" | string) => boolean =
    React.useCallback(
      (type: "transition" | "quiz" | string) => {
        if (isTakingScreenshot) return true;
        else if (type === "transition") return wayDisabled;
        else if (type === "quiz") return quizDisabled;
        else return hsDisabed;
      },
      [isTakingScreenshot, wayDisabled, quizDisabled, hsDisabed]
    );

  const otherBtnsDisabled: boolean = React.useMemo(() => {
    return (
      !!currentScene?.hotspots.find(
        (one: Hotspot) => one.isNew || one.EDIT_POS
      ) || isTakingScreenshot
    );
  }, [isTakingScreenshot, currentScene]);

  return (
    <div className="left-side-panel">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {Buttons.map((button: ButtonType) => (
          <button
            style={{
              position: "relative",
            }}
            key={button.type}
            id="icon"
            onClick={() => onHotspostAdd(button.type)}
            disabled={btnIsDisabled(button.type)}
          >
            {button.icon}

            {currentScene?.hotspots.filter(
              (hs: Hotspot) => hs.type === button.type
            ).length !== 0 && (
              <div style={counterStyle}>
                {
                  currentScene?.hotspots.filter(
                    (hs: Hotspot) => hs.type === button.type
                  ).length
                }
              </div>
            )}
          </button>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <hr />

        <button
          disabled={otherBtnsDisabled}
          title={getLanguageFile("button_labels.settings_map")}
          id="icon"
          className="gear"
          onClick={() => {
            mapEditor.setOpen(currentScene?.map);
          }}
        >
          <MapIcon width={"2.5em"} height={"2.5em"} />
        </button>
        <button
          disabled={otherBtnsDisabled}
          title={getLanguageFile("button_labels.settings_group")}
          id="icon"
          onClick={() => changeEditElem("groups")}
        >
          <GroupsIcon width="2em" height="2em" />
        </button>
        <button
          disabled={otherBtnsDisabled}
          title={getLanguageFile("button_labels.settings_tour")}
          id="icon"
          className="gear"
          onClick={() => changeEditElem("tour")}
        >
          <GearIcon width="2.5em" height="2.5em" />
        </button>
      </div>
    </div>
  );
};

export default LeftSidePanel;

const Buttons: ButtonType[] = [
  {
    type: "popup",
    icon: <PopupAddIcon width="3em" height="3em" color="#fff" />,
  },
  { type: "link", icon: <Link width="3.2em" height="3.2em" color="#fff" /> },
  {
    type: "quiz",
    icon: <QuizAddIcon width="3.5em" height="3.5em" color="#fff" />,
  },
  {
    type: "transition",
    icon: <RightArrow width="3em" height="3em" color="#fff" />,
  },
  { type: "audio", icon: <IconSound width={"3em"} height={"3em"} /> },
];

type ButtonType = {
  type: "transition" | "link" | "quiz" | "popup" | "audio";
  icon: JSX.Element;
};

const counterStyle: React.CSSProperties = {
  position: "absolute",
  left: 0,
  bottom: 0,
  width: 17,
  height: 17,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 50,
  background: "#6b7ef9",
  userSelect: "none",
  fontSize: ".7rem",
};
